import { useEffect, useRef } from 'react';
import { getWithAuth } from '@/utils/apiClient';
import { buildCompletionMessage } from '@/utils/notificationMessage';
import { logger } from '@/utils/logger';

interface PendingGeneration {
  requestId: string;
  sessionId?: string | null;
}

interface JobStatus {
  request_id: string;
  status: 'pending' | 'running' | 'complete' | 'error' | 'cancelled';
  note_ids?: string[];
  work_product_ids?: string[];
  message?: string;
  error?: string;
  attempt_count?: number;
}

interface UseAIGenerationJobRecoveryOptions {
  currentRequestId: string | null | undefined;
  sessionId?: string | null;
  setCurrentRequestId?: (id: string | null) => void;
  setIsProcessing?: (processing: boolean) => void;
  enabled?: boolean;
  storageNamespace?: string;
}

const buildStorageKey = (namespace: string) => `kindredly:pendingAIGeneration:${namespace}`;

function readPending(namespace: string): PendingGeneration | null {
  try {
    const raw = sessionStorage.getItem(buildStorageKey(namespace));
    if (!raw) return null;
    return JSON.parse(raw) as PendingGeneration;
  } catch {
    return null;
  }
}

function writePending(namespace: string, pending: PendingGeneration | null) {
  try {
    if (pending) {
      sessionStorage.setItem(buildStorageKey(namespace), JSON.stringify(pending));
    } else {
      sessionStorage.removeItem(buildStorageKey(namespace));
    }
  } catch {
    // Ignore storage failures (e.g. private mode).
  }
}

function dispatchComplete(requestId: string, sessionId: string | null | undefined, job: JobStatus) {
  const noteIds = job.note_ids || [];
  const batchNotes = noteIds.map((id) => ({ note_id: id, id }));
  const firstNoteId = noteIds[0];
  const message = buildCompletionMessage(job.message || 'Your note is ready.', firstNoteId);
  const detail = {
    requestId,
    sessionId,
    message,
    noteData: batchNotes[0] || null,
    batchNotes: batchNotes.length > 1 ? batchNotes : undefined,
  };
  window.dispatchEvent(new CustomEvent('soap_note_complete', { detail }));
}

function dispatchError(requestId: string, sessionId: string | null | undefined, message: string, errorType = 'error') {
  const detail = {
    requestId,
    sessionId,
    message: message || 'Note generation failed. Please try again.',
    errorType,
  };
  window.dispatchEvent(new CustomEvent('soap_note_error', { detail }));
}

export function useAIGenerationJobRecovery({
  currentRequestId,
  sessionId,
  setCurrentRequestId,
  setIsProcessing,
  enabled = true,
  storageNamespace = 'default',
}: UseAIGenerationJobRecoveryOptions) {
  const hasRestoredRef = useRef(false);

  // Track the latest currentRequestId so the polling cleanup can distinguish
  // the active request being cleared (reset spinner) from a new request id
  // taking its place (leave the spinner, the next poll will re-assert it).
  const currentRequestIdRef = useRef(currentRequestId);
  currentRequestIdRef.current = currentRequestId;

  // Restore a pending request from session storage on mount, but only once and
  // only if the stored session id matches the current session.
  useEffect(() => {
    if (!enabled || hasRestoredRef.current || currentRequestId) return;

    const pending = readPending(storageNamespace);
    if (!pending?.requestId) {
      hasRestoredRef.current = true;
      return;
    }

    if (!sessionId) {
      // We don't have a session id yet (e.g., modal just mounted), so we cannot
      // verify that the persisted request belongs to the current session. Do not
      // restore now; this effect will re-run once sessionId is set and can
      // validate ownership then.
      return;
    }

    if (pending.sessionId !== sessionId) {
      logger.log('[AI-RECOVERY] Ignoring pending request from a different session:', pending.requestId);
      writePending(storageNamespace, null);
      hasRestoredRef.current = true;
      return;
    }

    logger.log('[AI-RECOVERY] Restoring pending request from sessionStorage:', pending.requestId);
    setCurrentRequestId?.(pending.requestId);
    setIsProcessing?.(true);
    hasRestoredRef.current = true;
  }, [enabled, currentRequestId, sessionId, storageNamespace, setCurrentRequestId, setIsProcessing]);

  // Keep session storage in sync with the active request id.
  // Guard: do not clear/overwrite storage on initial mount before the restore
  // effect has inspected any pending request, otherwise a reload with a
  // not-yet-rehydrated session id can wipe the very pending entry we are
  // trying to recover.
  useEffect(() => {
    if (!enabled) return;
    if (currentRequestId) {
      writePending(storageNamespace, { requestId: currentRequestId, sessionId });
    } else if (hasRestoredRef.current) {
      writePending(storageNamespace, null);
    }
  }, [currentRequestId, sessionId, storageNamespace, enabled]);

  // Poll the durable job status endpoint while a request is active.
  useEffect(() => {
    if (!enabled || !currentRequestId) return;

    let timeoutId: number | undefined;
    let active = true;
    let consecutiveErrors = 0;
    const MAX_RETRY_ERRORS = 5;

    const schedulePoll = (delayMs: number) => {
      if (!active) return;
      timeoutId = window.setTimeout(poll, delayMs);
    };

    const poll = async () => {
      if (!active) return;

      try {
        const res = await getWithAuth(`/api/engine/jobs/${encodeURIComponent(currentRequestId)}`);
        // If the effect was cleaned up while the fetch was in flight, do not
        // overwrite state from a stale request.
        if (!active) return;

        if (!res.ok) {
          if (res.status === 404) {
            // The job no longer exists or the request id is stale.  Stop polling
            // and surface a terminal error so the UI is not stuck.
            logger.log('[AI-RECOVERY] Job not found for request:', currentRequestId);
            dispatchError(
              currentRequestId,
              sessionId,
              'The note generation request could not be found. Please try again.',
              'not_found',
            );
            setIsProcessing?.(false);
            setCurrentRequestId?.(null);
            writePending(storageNamespace, null);
            active = false;
            return;
          }

          logger.error('[AI-RECOVERY] Status fetch failed:', res.status);
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_RETRY_ERRORS) {
            dispatchError(
              currentRequestId,
              sessionId,
              'Unable to check note generation status. Please try again.',
              'status_poll_failed',
            );
            setIsProcessing?.(false);
            setCurrentRequestId?.(null);
            writePending(storageNamespace, null);
            active = false;
            return;
          }
          schedulePoll(5000);
          return;
        }

        consecutiveErrors = 0;
        const job = (await res.json()) as JobStatus;
        // If the effect was cleaned up while parsing the response, stop.
        if (!active) return;

        logger.log('[AI-RECOVERY] status=%s request=%s', job.status, currentRequestId);

        if (job.status === 'complete') {
          dispatchComplete(currentRequestId, sessionId, job);
          setIsProcessing?.(false);
          setCurrentRequestId?.(null);
          writePending(storageNamespace, null);
          active = false;
          return;
        }

        if (job.status === 'error' || job.status === 'cancelled') {
          dispatchError(currentRequestId, sessionId, job.error || job.message || 'Note generation failed.');
          setIsProcessing?.(false);
          setCurrentRequestId?.(null);
          writePending(storageNamespace, null);
          active = false;
          return;
        }

        // pending / running — keep polling
        if (!active) return;
        setIsProcessing?.(true);
        schedulePoll(3000);
      } catch (err) {
        // The effect may have been cleaned up while the request was in flight.
        if (!active) return;

        logger.error('[AI-RECOVERY] Poll error:', err);
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_RETRY_ERRORS) {
          dispatchError(
            currentRequestId,
            sessionId,
            'Unable to check note generation status. Please try again.',
            'status_poll_error',
          );
          setIsProcessing?.(false);
          setCurrentRequestId?.(null);
          writePending(storageNamespace, null);
          active = false;
          return;
        }
        schedulePoll(5000);
      }
    };

    poll();

    return () => {
      active = false;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      // When the active request id is cleared (completion, error, or cancellation),
      // reset the processing indicator so the UI is not left stuck in a disabled state.
      // If a new request id has already taken its place, leave the spinner for
      // the next effect/poll cycle to avoid a brief enabled flash.
      if (!currentRequestIdRef.current) {
        setIsProcessing?.(false);
      }
    };
  }, [currentRequestId, sessionId, storageNamespace, enabled, setCurrentRequestId, setIsProcessing]);
}
